<!-- src/components/Generics/PermissionView.vue -->
<script setup>
import { ref, computed, watch, onMounted } from "vue";

import { useNoteStore } from "@/stores/useNoteStore";
import { useTextStore } from "@/stores/useTextStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useUserStore } from "@/stores/useUserStore";
import { useGroupStore } from "@/stores/useGroupStore";
import { usePermissionStore } from "@/stores/usePermissionStore";

import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  entityType: {
    type: String,
    required: true,
  },
  entityId: {
    type: Number,
    default: null,
  },
  noteId: {
    type: Number,
    default: null,
  },
  textId: {
    type: Number,
    default: null,
  },
  activityId: {
    type: Number,
    default: null,
  },
  title: {
    type: String,
    default: "Permissões",
  },
});

const emit = defineEmits(["close"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

const noteStore = useNoteStore();
const textStore = useTextStore();
const activityStore = useActivityStore();
const userStore = useUserStore();
const groupStore = useGroupStore();
const permissionStore = usePermissionStore();

const isLoading = ref(false);
const loadError = ref("");
const permissionsRaw = ref([]);

function closeModal() {
  emit("close");
}

const normalizedEntityType = computed(() => {
  const raw = (props.entityType || "").toString().trim().toLowerCase();

  if (["note", "notes", "nota", "notas"].includes(raw)) return "note";
  if (["text", "texts", "texto", "textos"].includes(raw)) return "text";
  if (["activity", "activities", "atividade", "atividades"].includes(raw))
    return "activity";

  return raw;
});

const entityId = computed(() => {
  if (normalizedEntityType.value === "note") {
    return props.noteId ?? props.entityId ?? null;
  }

  if (normalizedEntityType.value === "text") {
    return props.textId ?? props.entityId ?? null;
  }

  if (normalizedEntityType.value === "activity") {
    return props.activityId ?? props.entityId ?? null;
  }

  return props.entityId ?? null;
});

function getUserById(userId) {
  return (userStore.users || []).find((u) => u.userId === userId) || null;
}

function getGroupById(groupId) {
  return (groupStore.groups || []).find((g) => g.groupId === groupId) || null;
}

function getUserNameById(userId) {
  const user = getUserById(userId);
  return user?.userName || `Usuário #${userId}`;
}

function getGroupNameById(groupId) {
  const group = getGroupById(groupId);
  return group?.groupName || `Grupo #${groupId}`;
}

function getPermissionNameById(permissionId) {
  const permission = (permissionStore.permissions || []).find(
    (p) => p.permissionId === permissionId
  );
  return permission?.permissionName || `Permissão #${permissionId}`;
}

function normalizeAvatarPath(path) {
  if (!path) return "";

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

function resolveUserPhotoPath(permissionRow, userId) {
  const directPayloadPath =
    permissionRow?.userPhotoPath ||
    permissionRow?.photoPath ||
    permissionRow?.user?.photoPath ||
    permissionRow?.user?.userPhotoPath ||
    permissionRow?.createdByUserPhotoPath ||
    permissionRow?.updatedByUserPhotoPath ||
    null;

  if (directPayloadPath) return directPayloadPath;

  const user = getUserById(userId);
  return user?.photoPath || user?.userPhotoPath || null;
}

const normalizedPermissions = computed(() => {
  const flatRows = (permissionsRaw.value || []).map((p, index) => {
    const userId = p.userId ?? null;
    const groupId = p.groupId ?? null;

    const hasUser =
      p.userId != null ||
      p.type === "user" ||
      p.type === "Usuário" ||
      (p.userName && p.userName.trim() !== "");

    const hasGroup =
      p.groupId != null ||
      p.type === "group" ||
      p.type === "Grupo" ||
      (p.groupName && p.groupName.trim() !== "");

    const type = hasUser ? "Usuário" : hasGroup ? "Grupo" : "—";

    const targetName = hasUser
      ? p.userName ||
        p.entityName ||
        (userId != null ? getUserNameById(userId) : "—")
      : hasGroup
      ? p.groupName ||
        p.entityName ||
        (groupId != null ? getGroupNameById(groupId) : "—")
      : "—";

    const permissionName =
      p.permissionName ||
      (p.permissionId != null ? getPermissionNameById(p.permissionId) : "—");

    return {
      rowKey:
        p.notePermissionId ??
        p.textPermissionId ??
        p.activityPermissionId ??
        `${type}-${targetName}-${permissionName}-${index}`,
      type,
      targetName,
      permissionName,
      photoPath: hasUser ? resolveUserPhotoPath(p, userId) : null,
    };
  });

  const groupedMap = new Map();

  for (const item of flatRows) {
    const groupKey = `${item.type}__${item.targetName}`;

    if (!groupedMap.has(groupKey)) {
      groupedMap.set(groupKey, {
        rowKey: groupKey,
        type: item.type,
        targetName: item.targetName,
        permissions: [],
        photoPath: item.photoPath,
      });
    }

    const current = groupedMap.get(groupKey);

    if (!current.photoPath && item.photoPath) {
      current.photoPath = item.photoPath;
    }

    if (
      item.permissionName &&
      item.permissionName !== "—" &&
      !current.permissions.includes(item.permissionName)
    ) {
      current.permissions.push(item.permissionName);
    }
  }

  return Array.from(groupedMap.values());
});

async function carregarDadosBase() {
  await Promise.all([
    userStore.fetchUsers(1, 1000, ""),
    groupStore.fetchGroups(1, 1000, ""),
    permissionStore.fetchAllPermissions(),
  ]);
}

async function carregarPermissoes() {
  if (!props.show) return;

  loadError.value = "";

  const type = normalizedEntityType.value;
  const id = entityId.value;

  const hasValidType = ["note", "text", "activity"].includes(type);
  const hasValidId = id !== null && id !== undefined && String(id).trim() !== "";

  if (!hasValidType || !hasValidId) {
    permissionsRaw.value = [];
    loadError.value = "Entidade não informada corretamente.";
    return;
  }

  isLoading.value = true;

  try {
    await carregarDadosBase();

    if (type === "note") {
      permissionsRaw.value = await noteStore.fetchNotePermissions(id);
    } else if (type === "text") {
      permissionsRaw.value = await textStore.fetchTextPermissions(id);
    } else if (type === "activity") {
      permissionsRaw.value = await activityStore.fetchActivityPermissions(id);
    } else {
      permissionsRaw.value = [];
      loadError.value = "Tipo de entidade inválido.";
    }
  } catch (error) {
    console.error("❌ Erro ao carregar permissões:", error);
    permissionsRaw.value = [];
    loadError.value =
      error.response?.data?.message ||
      error.message ||
      "Erro ao carregar permissões.";
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => [
    props.show,
    props.entityType,
    props.entityId,
    props.noteId,
    props.textId,
    props.activityId,
  ],
  async ([show]) => {
    if (show) {
      // 🔹 NOVO: recentraliza ao abrir — a posição do arrasto não persiste
      resetPosition();
      await carregarPermissoes();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  if (props.show) {
    await carregarPermissoes();
  }
});
</script>

<template>
  <div v-if="show" class="permission-view-backdrop" @click.self="closeModal">
    <div ref="panelRef" class="permission-view-panel" :style="panelStyle">
      <div
        class="permission-view-header"
        :class="{
 'permission-view-header--draggable': isDraggable,
          'permission-view-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="permission-view-title">{{ title }}</h3>

        <button
          @click="closeModal"
          type="button"
          class="permission-view-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div class="permission-view-body">
        <section class="permissions-section">
          <div class="permissions-section-header">
            <h4 class="permissions-section-title">Tipos de acesso</h4>
          </div>

          <div v-if="isLoading" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Carregando permissões...</span>
          </div>

          <div v-else-if="loadError" class="error-state">
            {{ loadError }}
          </div>

          <div v-else class="table-wrap">
            <table class="permissions-table">
              <thead>
                <tr>
                  <th class="col-name">Nome</th>
                  <th class="col-type">Tipo</th>
                  <th class="col-permissions">Permissões</th>
                </tr>
              </thead>

              <tbody v-if="normalizedPermissions.length">
                <tr v-for="item in normalizedPermissions" :key="item.rowKey">
                  <td class="target-name-cell">
                    <div
                      v-if="item.type === 'Usuário'"
                      class="name-with-avatar"
                    >
                      <div class="permission-avatar">
                        <div
                          v-if="getAvatarUrl(item.photoPath)"
                          class="permission-avatar-img"
                          :style="{
                            backgroundImage: `url('${getAvatarUrl(
                              item.photoPath
                            )}')`,
                          }"
                        ></div>

                        <i
                          v-else
                          class="fas fa-user permission-avatar-icon"
                        ></i>
                      </div>

                      <span class="target-name-text">
                        {{ item.targetName }}
                      </span>
                    </div>

                    <div v-else class="name-with-group-icon">
                      <span class="entity-icon-group" title="Grupo">
                        <i class="fas fa-users"></i>
                      </span>

                      <span class="target-name-text">
                        {{ item.targetName }}
                      </span>
                    </div>
                  </td>

                  <td class="type-cell">
                    {{ item.type }}
                  </td>

                  <td class="permissions-cell">
                    <div
                      v-if="item.permissions && item.permissions.length"
                      class="permission-badges"
                    >
                      <span
                        v-for="permission in item.permissions"
                        :key="`${item.rowKey}-${permission}`"
                        class="permission-badge"
                      >
                        {{ permission }}
                      </span>
                    </div>

                    <span v-else class="muted-text">—</span>
                  </td>
                </tr>
              </tbody>

              <tbody v-else>
                <tr>
                  <td colspan="3" class="empty-state">
                    Nenhuma permissão adicionada.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>

.permission-view-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10100;
  background-color: var(--backdrop);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 20px;
  padding-left: 2rem;
  padding-right: 2rem;
  padding-bottom: 2rem;
  overflow: hidden;
}

.permission-view-panel {
  width: 100%;
  max-width: 37.8rem;
  background-color: var(--bg-content);
  border-radius: 14px;
  box-shadow: var(--shadow-modal);
  display: flex;
  flex-direction: column;
  height: 344px;
  max-height: 344px;
  overflow: hidden;
}

.permission-view-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
}

/* 🔹 NOVO: cabeçalho como alça de arrasto do painel.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.permission-view-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.permission-view-header--dragging {
  cursor: grabbing;
}
/* O botão de fechar continua sendo clique, não alça */
.permission-view-header--draggable .permission-view-close-btn {
  cursor: pointer;
}

.permission-view-title {
  font-size: 1.02rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.permission-view-close-btn {
  color: var(--text-light);
  background-color: transparent;
  border-radius: 8px;
  padding: 0.375rem;
  transition: background-color 0.2s, color 0.2s;
  border: none;
  cursor: pointer;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.permission-view-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: var(--text-dark);
}

.permission-view-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0.75rem 0.85rem 0.85rem 0.85rem;
  background-color: var(--bg-content);
  overflow: hidden;
}

.permissions-section {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.permissions-section-header {
  margin-bottom: 0.45rem;
}

.permissions-section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.loading-state,
.error-state,
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 90px;
  color: var(--text-light);
  font-size: 0.86rem;
  gap: 0.45rem;
}

.error-state {
  color: var(--danger-text);
}

.table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  background: var(--surface-2);
}

.permissions-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.permissions-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--surface-3);
  color: var(--text-dark);
  font-size: 0.74rem;
  font-weight: 700;
  text-align: left;
  padding: 0.5rem 0.65rem;
  border-bottom: 1px solid var(--neutral-200);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.permissions-table tbody td {
  padding: 0.45rem 0.65rem;
  border-bottom: 1px solid var(--neutral-200);
  color: var(--text-dark);
  font-size: 0.8rem;
  vertical-align: middle;
  background: var(--surface);
  line-height: 1.2;
}

.permissions-table tbody tr:last-child td {
  border-bottom: none;
}

.col-name {
  width: 210px;
}

.col-type {
  width: 86px;
}

.col-permissions {
  width: auto;
}

.type-cell {
  white-space: nowrap;
  font-weight: 600;
  color: var(--text-dark);
}

.target-name-cell {
  font-weight: 500;
  color: #0f172a;
  word-break: break-word;
}

.name-with-avatar,
.name-with-group-icon {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.target-name-text {
  display: inline-block;
  min-width: 0;
  word-break: break-word;
}

.permissions-cell {
  padding-top: 0.36rem !important;
  padding-bottom: 0.36rem !important;
}

.entity-icon-group {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background-color: var(--border-color);
  color: var(--text-muted);
  border: 1px solid var(--border-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: 0 0 26px;
}

.entity-icon-group i {
  font-size: 13px;
}

.permission-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.permission-badge {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0.06rem 0.42rem;
  border-radius: 999px;
  background: var(--neutral-100);
  border: 1px solid var(--border-color);
  color: var(--text-dark);
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1.05;
}

.muted-text {
  color: var(--text-subtle);
  font-style: italic;
}

.table-wrap::-webkit-scrollbar {
  width: 7px;
  height: 7px;
}

.table-wrap::-webkit-scrollbar-thumb {
  background-color: var(--neutral-400);
  border-radius: 999px;
}

.table-wrap::-webkit-scrollbar-track {
  background: transparent;
}

.permission-avatar {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background-color: var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 26px;
}

.permission-avatar-img {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

.permission-avatar-icon {
  font-size: 13px;
  color: var(--text-muted);
}
</style>